import React, { useState } from 'react';
import { BannerSet, DataFeed } from '../types';
import { X, Upload, Save, Trash2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog } from './ConfirmDialog';
import { dataService } from '../services/dataService';

interface BannerSetSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bannerSet: BannerSet;
  onUpdateBannerSet: (updates: Partial<BannerSet>) => void;
}

export const BannerSetSettingsModal: React.FC<BannerSetSettingsModalProps> = ({
  isOpen,
  onClose,
  bannerSet,
  onUpdateBannerSet
}) => {
  const [name, setName] = useState(bannerSet.name);
  const [status, setStatus] = useState(bannerSet.status);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [dataFeedUrl, setDataFeedUrl] = useState(bannerSet.dataFeedUrl || '');
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(bannerSet.autoRefreshInterval || 5); // Default 5 minutes
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(bannerSet.lastRefreshTime || null);
  const [refreshTimer, setRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  const [showManualUpload, setShowManualUpload] = useState(false);
  
  const { showSuccess, showError, showWarning, showInfo } = useToast();

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      showError('Validation Error', 'Banner set name is required');
      return;
    }

    setIsLoading(true);
    try {
      // Only send the fields that have changed
      const updates: Partial<BannerSet> = {};
      
      if (name.trim() !== bannerSet.name) {
        updates.name = name.trim();
      }
      
      if (status !== bannerSet.status) {
        updates.status = status;
      }
      
      if (dataFeedUrl.trim() !== bannerSet.dataFeedUrl) {
        updates.dataFeedUrl = dataFeedUrl.trim();
      }
      
      if (Number(autoRefreshInterval) !== bannerSet.autoRefreshInterval) {
        updates.autoRefreshInterval = Number(autoRefreshInterval);
      }
      
      updates.lastModified = new Date();

      if (lastRefreshTime) {
        updates.lastRefreshTime = lastRefreshTime;
      }

      // Only make the API call if there are actual changes
      if (Object.keys(updates).length > 0) {
        try {
          // Update backend first
          const updatedBannerSet = await dataService.updateBannerSet(bannerSet.id, updates);
          
          // Then update local state with the response from the server
          onUpdateBannerSet(updatedBannerSet);
          
          showSuccess('Settings Saved', 'Banner set settings have been updated successfully');
          onClose();
        } catch (error: any) {
          // Handle specific error cases
          if (error.message.includes('Authentication required')) {
            showError('Session Expired', 'Your session has expired. Please log in again.');
            // Optionally redirect to login page
            window.location.href = '/login';
          } else if (error.message.includes('No response from server')) {
            showError('Connection Error', 'Unable to connect to the server. Please check your internet connection and try again.');
          } else if (error.message.includes('Conflict')) {
            showError('Update Conflict', 'The banner set has been modified by another user. Please refresh and try again.');
          } else {
            showError('Save Failed', error.message || 'Failed to save settings. Please try again.');
          }
        }
      } else {
        showInfo('No Changes', 'No changes were made to save');
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to save banner set settings:', error);
      showError('Save Failed', error.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch data from URL
  const fetchDataFromUrl = async (url: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      let processedData: DataFeed[];
      if (Array.isArray(data)) {
        processedData = data;
      } else {
        processedData = [data];
      }
      
      const now = new Date();
      onUpdateBannerSet({
        dataFeed: processedData[0] || null,
        dataFeedArray: processedData,
        dataFeedUrl: url,
        lastRefreshTime: now,
        lastModified: now
      });
      
      setLastRefreshTime(now);
      showSuccess(
        'Data Feed Updated', 
        `Successfully loaded ${processedData.length} data item${processedData.length !== 1 ? 's' : ''}`
      );
    } catch (error) {
      showError(
        'Data Feed Error', 
        'Failed to fetch data from URL. Please check the URL and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Function to start auto-refresh timer
  const startAutoRefresh = () => {
    if (refreshTimer) {
      clearInterval(refreshTimer);
    }
    
    if (dataFeedUrl && autoRefreshInterval > 0) {
      const timer = setInterval(() => {
        fetchDataFromUrl(dataFeedUrl);
      }, autoRefreshInterval * 60 * 1000);
      setRefreshTimer(timer);
    }
  };

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (refreshTimer) {
        clearInterval(refreshTimer);
      }
    };
  }, [refreshTimer]);

  const handleDataFeedUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        
        let processedData: DataFeed[];
        if (Array.isArray(data)) {
          processedData = data;
        } else {
          processedData = [data];
        }
        
        onUpdateBannerSet({
          dataFeed: processedData[0] || null,
          dataFeedArray: processedData,
          lastModified: new Date()
        });
        
        setIsUploading(false);
        showSuccess(
          'Data Feed Uploaded', 
          `Successfully uploaded ${processedData.length} data item${processedData.length !== 1 ? 's' : ''}`
        );
      } catch (error) {
        setIsUploading(false);
        showError(
          'Upload Failed', 
          'Invalid JSON file. Please check the format and try again.'
        );
      }
    };
    
    reader.onerror = () => {
      setIsUploading(false);
      showError('Upload Failed', 'Error reading file. Please try again.');
    };
    
    reader.readAsText(file);
  };

  const handleRemoveDataFeed = () => {
    onUpdateBannerSet({
      dataFeed: null,
      dataFeedArray: [],
      lastModified: new Date()
    });
    setShowRemoveConfirm(false);
    showWarning('Data Feed Removed', 'The data feed has been removed from this banner set');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Banner Set Settings</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Set Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter banner set name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as BannerSet['status'])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Data Feed Management */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Data Feed Management</h3>
              
              <div className="space-y-4">
                {/* Current Data Feed Status */}
                {bannerSet.dataFeedArray.length > 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="font-medium text-green-800">
                          Data Feed Active ({bannerSet.dataFeedArray.length} items)
                        </span>
                      </div>
                      <button
                        onClick={() => setShowRemoveConfirm(true)}
                        className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors"
                        title="Remove Data Feed"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    <p className="text-sm text-green-700">
                      Data feed is connected and ready for use with banner previews, exports, and deployments.
                      {lastRefreshTime && (
                        <span className="block mt-1">
                          Last refreshed: {lastRefreshTime.toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="font-medium text-yellow-800">No Data Feed</span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      Enter a JSON URL or upload a file to enable dynamic content and batch exports.
                    </p>
                  </div>
                )}

                {/* Data Feed URL Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {bannerSet.dataFeedArray.length > 0 ? 'Update Data Feed URL' : 'Data Feed URL'}
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="url"
                      value={dataFeedUrl}
                      onChange={(e) => setDataFeedUrl(e.target.value)}
                      placeholder="https://app.dealercentives.com/auto/feeds/all/all/all/75203"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => fetchDataFromUrl(dataFeedUrl)}
                      disabled={isLoading || !dataFeedUrl}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Loading...' : 'Load Data'}
                    </button>
                  </div>
                </div>

                {/* Auto-refresh Settings */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Auto-refresh Interval (minutes)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={autoRefreshInterval}
                      onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={startAutoRefresh}
                      disabled={!dataFeedUrl || autoRefreshInterval < 1}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Enable Auto-refresh
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Data will be automatically refreshed every {autoRefreshInterval} minutes
                  </p>
                </div>

                {/* Manual Upload Option */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Manual Upload (Alternative)
                    </label>
                    <button
                      onClick={() => setShowManualUpload(!showManualUpload)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {showManualUpload ? 'Hide' : 'Show'} Manual Upload
                    </button>
                  </div>
                  
                  {showManualUpload && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="file"
                          accept=".json"
                          onChange={handleDataFeedUpload}
                          disabled={isUploading}
                          className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                        />
                        {isUploading && (
                          <div className="flex items-center space-x-2 text-blue-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm">Uploading...</span>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Upload a JSON file containing an array of data objects or a single data object.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Banner Set Statistics */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Total Sizes</div>
                  <div className="text-lg font-semibold text-gray-900">{bannerSet.sizes.length}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Completed</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {bannerSet.sizes.filter(size => size.elements.length > 0).length}
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Data Items</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {bannerSet.dataFeedArray.length}
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">Status</div>
                  <div className={`text-lg font-semibold capitalize ${
                    status === 'active' ? 'text-green-600' :
                    status === 'draft' ? 'text-yellow-600' :
                    'text-gray-600'
                  }`}>
                    {status}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save size={16} />
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>

      {/* Remove Data Feed Confirmation */}
      <ConfirmDialog
        isOpen={showRemoveConfirm}
        title="Remove Data Feed"
        message="Are you sure you want to remove the data feed? This will affect all banner previews and exports for this banner set."
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleRemoveDataFeed}
        onCancel={() => setShowRemoveConfirm(false)}
      />
    </>
  );
};